import React, { useMemo, useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Container, Row, Col, Button, Spinner, Alert } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import "./admin-lo-listing-calendar.scss";
import { v4 as uuidv4 } from 'uuid';

import ListingCalendarService from "../../../../services/listing-calendar-service";
import ReservationRequestModal from "./components/modals/reservationRequest";
import BlockDateModal from "./components/modals/block-date-modal/block-date-modal";
import useReservations from "../../../../hooks/use-reservations";
import { buildMonthGrid, formatYearMonth, parseYearMonthToken, formatDateKey } from "../../../../helpr/date-utils";
import CalendarGrid from "./components/listing-calendar/calendar-grid";
import CalendarLegend from "../../../../components/listing-calendar/calendar-legend/calendar-legend";

const URL_PARAM = "month";

const AdminLoListingCalendar = () => {
  const [searchParams] = useSearchParams();

  const getMonthFromParams = (params) => {
    const parsed = parseYearMonthToken(params.get(URL_PARAM));
    if (parsed) return parsed;
    const now = new Date();
    return { year: now.getFullYear(), monthIndex: now.getMonth() };
  };

  const initialMonth = getMonthFromParams(searchParams);
  const [currentYear, setCurrentYear] = useState(initialMonth.year);
  const [currentMonthIndex, setCurrentMonthIndex] = useState(initialMonth.monthIndex);

  useEffect(() => {
    const { year, monthIndex } = getMonthFromParams(searchParams);
    setCurrentYear(year);
    setCurrentMonthIndex(monthIndex);
  }, [searchParams]);

  const [selectedDateKey, setSelectedDateKey] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalMode, setModalMode] = useState("view");
  const [selectedBlockedItem, setSelectedBlockedItem] = useState(null);

  const listingCalendarService = useMemo(() => new ListingCalendarService(), []);

  const { reservations, setReservations, loading, error } = useReservations(listingCalendarService);

  // local state for blocked-dates fetch (server-provided blocked/holiday list)
  const [blockedItems, setBlockedItems] = useState([]);
  const [blockedLoading, setBlockedLoading] = useState(false);
  const [blockedError, setBlockedError] = useState(null);

  useEffect(() => {
    const token = formatYearMonth(currentYear, currentMonthIndex);
    if (searchParams.get(URL_PARAM) === token) return;
    try {
      const url = new URL(window.location.href);
      url.searchParams.set(URL_PARAM, token);
      window.history.replaceState({}, "", url.toString());
    } catch {}
  }, [currentYear, currentMonthIndex]);

  const weeks = useMemo(
    () => buildMonthGrid(currentYear, currentMonthIndex),
    [currentYear, currentMonthIndex]
  );

  function normalizeApiBlockedItem(apiItem, targetYears = [currentYear - 1, currentYear, currentYear + 1]) {
    const normalizedOutput = [];
    const rawKey = apiItem.listingDateKey || apiItem.listingDate || apiItem.date || null;

    const makeEntry = (dateKey, yearSuffixIfNeeded, markRegular = false) => {
      const idBase = apiItem.id || `BLOCKDATE#${uuidv4().replace(/-/g,'')}`;
      const id = String(idBase) + (yearSuffixIfNeeded ? `#${yearSuffixIfNeeded}` : "");
      return {
        id,
        sourceId: apiItem.id || null,
        listingDateKey: dateKey,
        isHoliday: !!apiItem.isHoliday,
        isRegular: !!apiItem.isRegular,
        notes: apiItem.reason || apiItem.notes || "",
        admin: apiItem.admin,
        status: "blocked",
        raw: apiItem,
      };
    };

    if (rawKey && /^\d{4}-\d{2}-\d{2}$/.test(rawKey)) {
      normalizedOutput.push(makeEntry(rawKey, null, false));
      return normalizedOutput;
    }

    if (rawKey) {
      try {
        const parsedDate = new Date(rawKey);
        if (!isNaN(parsedDate.getTime())) {
          normalizedOutput.push(makeEntry(formatDateKey(parsedDate), null, !!apiItem.isRegular));
          return normalizedOutput;
        }
      } catch {}
    }

    return normalizedOutput;
  }

  // load blocked items
  useEffect(() => {
    let mounted = true;
    async function loadBlocked() {
      setBlockedLoading(true);
      setBlockedError(null);
      try {
        const role = "listing officer";
        const res = await listingCalendarService.getBlockedDates(role);
        const listFromApi = (res && res.blockDates) ? res.blockDates : (res && res.blockedDates) ? res.blockedDates : [];
        const targetYears = [currentYear - 1, currentYear, currentYear + 1];

        const normalized = [];
        listFromApi.forEach((apiBlocked) => {
          try {
            const entries = normalizeApiBlockedItem(apiBlocked, targetYears);
            entries.forEach((entry) => normalized.push(entry));
          } catch (normalizeError) {
            console.warn("Failed to normalize blocked item", apiBlocked, normalizeError);
          }
        });

        if (!mounted) return;

        const seen = new Set();
        const deduplicated = [];
        normalized.forEach((normalizedItem) => {
          const dedupeKey = `${normalizedItem.listingDateKey}::${normalizedItem.sourceId || normalizedItem.notes || normalizedItem.id}`;
          if (!seen.has(dedupeKey)) {
            seen.add(dedupeKey);
            deduplicated.push(normalizedItem);
          }
        });

        if (!mounted) return;
        setBlockedItems(deduplicated);
      } catch (err) {
        if (!mounted) return;
        console.error("Failed to load blocked dates", err);
        setBlockedError(err);
      } finally {
        if (!mounted) return;
        setBlockedLoading(false);
      }
    }
    loadBlocked();
    return () => { mounted = false; };
  }, [listingCalendarService, currentYear, currentMonthIndex]);

  const reservationsMap = useMemo(() => {
    const mapByDate = {};

    function ensureDateKeyExists(dateKey) {
      if (!mapByDate[dateKey]) mapByDate[dateKey] = [];
    }

    function pushOccurrence(dateKey, occurrence) {
      ensureDateKeyExists(dateKey);
      mapByDate[dateKey].push(occurrence);
    }

    reservations.forEach((reservation) => {
      try {
        if (reservation && reservation.listingDates) {
          if (Array.isArray(reservation.listingDates)) {
            reservation.listingDates.forEach((listingDateItem) => {
              const listingToken = listingDateItem?.date ?? listingDateItem;
              if (!listingToken) return;
              try {
                const dateObject = new Date(listingToken);
                if (isNaN(dateObject.getTime())) return;
                const occurrenceDateKey = formatDateKey(dateObject);
                pushOccurrence(occurrenceDateKey, {
                  id: reservation.id,
                  institution: reservation.institution,
                  email: reservation.email,
                  notes: reservation.notes,
                  status: listingDateItem?.status || reservation.status || "pending",
                  listingDateOriginal: listingDateItem?.date || String(listingDateItem),
                  listingDateKey: occurrenceDateKey,
                  listingDateStatus: listingDateItem?.status || reservation.status || "pending",
                  raw: reservation,
                });
              } catch {}
            });
          } else if (typeof reservation.listingDates === "string") {
            const tokens = reservation.listingDates.split(/[,;]+/).map((t) => t.trim()).filter(Boolean);
            tokens.forEach((tokenStr) => {
              try {
                const parsedDate = new Date(tokenStr);
                if (isNaN(parsedDate.getTime())) return;
                const occurrenceDateKey = formatDateKey(parsedDate);
                pushOccurrence(occurrenceDateKey, {
                  id: reservation.id,
                  institution: reservation.institution,
                  email: reservation.email,
                  notes: reservation.notes,
                  status: reservation.status,
                  listingDateOriginal: tokenStr,
                  listingDateKey: occurrenceDateKey,
                  listingDateStatus: reservation.status || "pending",
                  raw: reservation,
                });
              } catch {}
            });
          }
        }

        if (reservation && reservation.status === "blocked") {
          let blockedKey = reservation.listingDateKey || null;

          if (!blockedKey && typeof reservation.id === "string") {
            const match = reservation.id.match(/([0-9]{4}-[0-9]{2}-[0-9]{2})/);
            if (match) blockedKey = match[1];
          }

          if (!blockedKey && reservation.date) {
            try {
              const parsedDate = new Date(reservation.date);
              if (!isNaN(parsedDate.getTime())) blockedKey = formatDateKey(parsedDate);
            } catch {}
          }

          if (blockedKey) {
            pushOccurrence(blockedKey, {
              id: reservation.id,
              notes: reservation.notes || "",
              status: "blocked",
              isHoliday: typeof reservation.isHoliday === "boolean" ? reservation.isHoliday : !!reservation.holiday,
              listingDateKey: blockedKey,
              raw: reservation,
            });
          }
        }
      } catch (processingError) {
        console.warn("Error processing reservation entry for calendar map", processingError, reservation);
      }
    });

    blockedItems.forEach((blockedItem) => {
      if (!blockedItem || !blockedItem.listingDateKey) return;
      try {
        let dateKey = blockedItem.listingDateKey;
        if (!/^\d{4}-\d{2}-\d{2}$/.test(dateKey)) {
          try {
            const parsedDate = new Date(dateKey);
            if (!isNaN(parsedDate.getTime())) dateKey = formatDateKey(parsedDate);
            else return;
          } catch { return; }
        }

        pushOccurrence(dateKey, {
          id: blockedItem.id,
          notes: blockedItem.notes || "",
          status: "blocked",
          isHoliday: !!blockedItem.isHoliday,
          isRegular: !!blockedItem.isRegular,
          listingDateKey: dateKey,
          admin: blockedItem.admin,
          raw: blockedItem.raw || blockedItem,
        });
      } catch (e) {
        console.warn("Error adding blocked item to map", blockedItem, e);
      }
    });

    return mapByDate;
  }, [reservations, blockedItems]);

  const monthLabel = new Date(currentYear, currentMonthIndex, 1).toLocaleString(undefined, {
    month: "long",
    year: "numeric",
  });

  function goToPreviousMonth() {
    const dt = new Date(currentYear, currentMonthIndex - 1, 1);
    setCurrentYear(dt.getFullYear());
    setCurrentMonthIndex(dt.getMonth());
  }
  function goToNextMonth() {
    const dt = new Date(currentYear, currentMonthIndex + 1, 1);
    setCurrentYear(dt.getFullYear());
    setCurrentMonthIndex(dt.getMonth());
  }

  function openModalForDate(date) {
    const dateKey = formatDateKey(date);
    const occurrences = reservationsMap[dateKey] || [];

    const blockedOccurrence = occurrences.find((occ) => occ && occ.status === "blocked");
    if (blockedOccurrence) {
      setSelectedDateKey(dateKey);
      setSelectedBlockedItem(blockedOccurrence);
      setModalMode("blocked");
      setIsModalVisible(true);
      return;
    }

    if (occurrences.length > 0) {
      setSelectedDateKey(dateKey);
      setSelectedBlockedItem(null);
      setModalMode("view");
      setIsModalVisible(true);
    } else {
      setSelectedDateKey(dateKey);
      setSelectedBlockedItem(null);
      setModalMode("block");
      setIsModalVisible(true);
    }
  }

  function handleBlockDate(listingDateKey, { reason, isHoliday, admin } = {}) {
    const createdBlockedItem = {
      id: `BLOCKDATE#${uuidv4().replace(/-/g, '')}`,
      listingDateKey,
      isHoliday: !!isHoliday,
      notes: reason || "",
      status: "blocked",
      admin,
    };

    setReservations((previousReservations) => {
      const exists = previousReservations.some((existing) => existing && existing.status === "blocked" && existing.listingDateKey === listingDateKey);
      if (exists) return previousReservations;
      return [...previousReservations, createdBlockedItem];
    });

    setBlockedItems((prev) => {
      const exists = prev.some((b) => b && (b.listingDateKey === listingDateKey));
      if (exists) return prev;
      return [...prev, createdBlockedItem];
    });

    setIsModalVisible(false);
    setSelectedDateKey(null);
    setSelectedBlockedItem(null);
    setModalMode("view");

    return createdBlockedItem;
  }

  function rollbackBlock(listingDateKey, optimisticId) {
    setBlockedItems((prev) => prev.filter((p) => !(p && (p.listingDateKey === listingDateKey && String(p.id) === String(optimisticId)))));
    setReservations((prev) => prev.filter((r) => !(r && r.status === "blocked" && r.listingDateKey === listingDateKey && String(r.id) === String(optimisticId))));
  }

  function handleUnblock(listingDateKey, blockedItemId) {
    const removed = blockedItems.find((b) => b && (b.id === blockedItemId || b.sourceId === blockedItemId || (b.listingDateKey === listingDateKey && String(b.id).includes(blockedItemId))));
    setBlockedItems((prev) => prev.filter((existing) => !(existing && (existing.id === blockedItemId || existing.sourceId === blockedItemId || (existing.listingDateKey === listingDateKey)))));
    setReservations((prev) => {
      return prev.filter((reservation) => {
        if (!reservation) return true;
        if (reservation.status === "blocked" && (reservation.listingDateKey === listingDateKey || (reservation.id && String(reservation.id).includes(listingDateKey)))) {
          return false;
        }
        return true;
      });
    });

    setSelectedBlockedItem(null);
    setSelectedDateKey(null);
    setIsModalVisible(false);
    setModalMode("view");

    return removed || null;
  }

  function rollbackUnblock(previousBlockedItem) {
    if (!previousBlockedItem) return;
    setBlockedItems((prev) => {
      const exists = prev.some((b) => b && b.id === previousBlockedItem.id);
      if (exists) return prev;
      return [...prev, previousBlockedItem];
    });
    setReservations((prev) => {
      const exists = prev.some((r) => r && r.id === previousBlockedItem.id);
      if (exists) return prev;
      return [...prev, { id: previousBlockedItem.id, listingDateKey: previousBlockedItem.listingDateKey, status: "blocked", notes: previousBlockedItem.notes, isHoliday: previousBlockedItem.isHoliday }];
    });
  }

  function onBlockSuccess(optimisticId, serverBlocked) {
    if (!serverBlocked) {
      try { window.location.reload(); } catch {}
      return;
    }

    const serverItems = Array.isArray(serverBlocked) ? serverBlocked : [serverBlocked];
    const normalized = [];
    serverItems.forEach((item) => {
      try {
        const entries = normalizeApiBlockedItem(item, [currentYear - 1, currentYear, currentYear + 1]);
        entries.forEach((e) => normalized.push(e));
      } catch (e) { console.warn("Failed to normalize server blocked item", e, item); }
    });

    if (normalized.length === 0) {
      try { window.location.reload(); } catch {}
      return;
    }

    setBlockedItems((prev) => {
      const removedOptimistic = prev.filter((p) => !(p && String(p.id) === String(optimisticId)));
      // avoid duplicates
      const merged = [...removedOptimistic];
      normalized.forEach((n) => {
        if (!merged.some((m) => m && (m.id === n.id || (m.listingDateKey === n.listingDateKey && m.notes === n.notes)))) {
          merged.push(n);
        }
      });
      return merged;
    });

    setReservations((prev) => {
      const filtered = prev.filter((r) => !(r && r.status === "blocked" && String(r.id) === String(optimisticId)));
      const toAdd = normalized.map((n) => ({ id: n.id, listingDateKey: n.listingDateKey, status: "blocked", notes: n.notes, isHoliday: n.isHoliday }));
      // ensure no duplicates
      toAdd.forEach((candidate) => {
        if (!filtered.some((f) => f && f.id === candidate.id && f.listingDateKey === candidate.listingDateKey)) filtered.push(candidate);
      });
      return filtered;
    });
  }

  function onUnblockSuccess(blockedItemId) {
    if (!blockedItemId) {
      try { window.location.reload(); } catch {}
      return;
    }

    setBlockedItems((prev) => prev.filter((b) => !(b && (b.id === blockedItemId || b.sourceId === blockedItemId))));
    setReservations((prev) => prev.filter((r) => !(r && r.status === "blocked" && (r.listingDateKey === (r.listingDateKey) && (String(r.id) === String(blockedItemId) || String(r.id).includes(blockedItemId))))));
  }

  const reservationsForSelectedDate = selectedDateKey ? reservationsMap[selectedDateKey] || [] : [];

  // legend items
  const legendItems = [
    { key: 'reservation', label: 'Reservation' },
    { key: 'blocked', label: 'Blocked (regular)' },
    { key: 'holiday', label: 'Holiday / Blocked (holiday)' }
  ];

  return (
    <Container className="adminlo-calendar-container p-3">
      <div className="adminlo-calendar-card">
        <Row className="align-items-center calendar-header mb-2">
          <Col xs="auto">
            <Button variant="outline-secondary" size="sm" onClick={goToPreviousMonth}>
              {"<"}
            </Button>
          </Col>
          <Col className="text-center">
            <strong>{monthLabel}</strong>
          </Col>
          <Col xs="auto">
            <Button variant="outline-secondary" size="sm" onClick={goToNextMonth}>
              {">"}
            </Button>
          </Col>
        </Row>

        {/* Legends: replaced with CalendarLegend component */}
        <Row className="mb-2">
          <Col>
            <CalendarLegend items={legendItems} />
          </Col>
        </Row>

        {(loading || blockedLoading) && (
          <div className="text-center my-2">
            <Spinner animation="border" size="sm" /> Loading reservations...
          </div>
        )}
        {error && (
          <div className="mb-2">
            <Alert variant="danger" className="py-1">
              {String(error)}
            </Alert>
          </div>
        )}
        {blockedError && (
          <div className="mb-2">
            <Alert variant="warning" className="py-1">
              Failed to load blocked dates.
            </Alert>
          </div>
        )}

        <CalendarGrid
          weeks={weeks}
          currentMonthIndex={currentMonthIndex}
          reservationsMap={reservationsMap}
          onCellClick={openModalForDate}
        />
      </div>

      {modalMode === "view" && (
        <ReservationRequestModal
          show={isModalVisible}
          onHide={() => setIsModalVisible(false)}
          reservationsList={reservationsForSelectedDate}
          onAccept={(id, key) => {/* unchanged */}}
          onReject={(id, key) => {/* unchanged */}}
          selectedDateKey={selectedDateKey}
        />
      )}

      {modalMode === "block" && (
        <BlockDateModal
          show={isModalVisible}
          onHide={() => setIsModalVisible(false)}
          selectedDateKey={selectedDateKey}
          onBlock={handleBlockDate}
          onBlockRollback={rollbackBlock}
          onBlockSuccess={onBlockSuccess}
        />
      )}

      {modalMode === "blocked" && (
        <BlockDateModal
          show={isModalVisible}
          onHide={() => setIsModalVisible(false)}
          selectedDateKey={selectedDateKey}
          blockedItem={selectedBlockedItem}
          readOnly={true}
          onUnblock={handleUnblock}
          onUnblockRollback={rollbackUnblock}
          onUnblockSuccess={onUnblockSuccess}
        />
      )}
    </Container>
  );
};

export default AdminLoListingCalendar;